import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faExclamationTriangle, 
  faArrowLeft, 
  faHome,
  faCompass,
  faMapMarkerAlt,
  faRoute,
  faBinoculars
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import PageHeader from "../componentes/PageHeader";

const NotFound = () => {
  const { t } = useTranslation();
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.2,
        delayChildren: 0.3
      } 
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };

  const floatingVariants = {
    initial: { y: 0 },
    animate: {
      y: [0, -15, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut"
      }
    }
  };

 

  return (
    <>
      <PageHeader />
      
      <div className="bg-white min-h-screen mt-20 pt-24 pb-16 px-dynamic relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-r from-primary/10 to-blue-100/30"></div>
        <div className="absolute bottom-0 right-0 w-full h-32 bg-gradient-to-l from-primary/10 to-blue-100/30"></div>
        <div className="absolute w-72 h-72 rounded-full bg-blue-50 -top-20 -right-20"></div>
        <div className="absolute w-96 h-96 rounded-full bg-primary/5 -bottom-40 -left-20"></div>
        
        {/* Main content */}
        <div className="relative z-10 max-w-5xl mx-auto flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-16">
          {/* Left side - Illustration */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full lg:w-1/2 flex justify-center"
          >
            <div className="relative">
              {/* Main illustration */}
              <motion.div
                variants={floatingVariants}
                initial="initial"
                animate="animate"
                className="relative z-10"
              >
                <svg width="350" height="350" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <motion.path 
                    d="M250 50C150 50 50 150 50 250C50 350 150 450 250 450C350 450 450 350 450 250C450 150 350 50 250 50Z" 
                    fill="#f0f9ff" 
                    stroke="#2Ba8CB" 
                    strokeWidth="5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                  />
                  <motion.path 
                    d="M250 125C231.25 125 212.5 131.25 196.875 143.75C181.25 156.25 168.75 175 162.5 193.75C156.25 212.5 156.25 233.75 162.5 252.5C168.75 271.25 181.25 287.5 196.875 300C212.5 312.5 231.25 318.75 250 318.75C268.75 318.75 287.5 312.5 303.125 300C318.75 287.5 331.25 271.25 337.5 252.5C343.75 233.75 343.75 212.5 337.5 193.75C331.25 175 318.75 156.25 303.125 143.75C287.5 131.25 268.75 125 250 125Z" 
                    fill="white" 
                    stroke="#2Ba8CB" 
                    strokeWidth="3"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, ease: "easeInOut", delay: 0.5 }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative">
                    <h2 className="text-[120px] font-bold text-gray-100 select-none">404</h2>
                    <motion.div 
                      className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.8, delay: 1 }}
                    >
                      <FontAwesomeIcon 
                        icon={faCompass} 
                        className="text-primary text-4xl"
                        spin 
                      />
                    </motion.div>
                  </div>
                </div>
              </motion.div>
              
              {/* Floating small icons */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.5, duration: 0.8 }}
                className="absolute -top-8 -left-8"
              >
                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-red-400 text-2xl" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.7, duration: 0.8 }}
                className="absolute top-1/3 -right-12"
              >
                <FontAwesomeIcon icon={faRoute} className="text-green-400 text-2xl" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.9, duration: 0.8 }}
                className="absolute -bottom-4 left-1/4"
              >
                <FontAwesomeIcon icon={faBinoculars} className="text-yellow-400 text-2xl" />
              </motion.div>
            </div>
          </motion.div>
          
          {/* Right side - Content */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="w-full lg:w-1/2 text-center lg:text-left"
          >
            <motion.div
              variants={itemVariants}
              className="inline-block bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6"
            >
              404 Error
            </motion.div>
            
            <motion.h1 
              variants={itemVariants}
              className="text-gray-800 text-4xl md:text-5xl font-catamaran font-bold mb-6 leading-tight"
            >
              {t("notfound.page_not_found") || "Page Not Found"}
            </motion.h1>
            
            <motion.p 
              variants={itemVariants}
              className="text-gray-600 text-lg font-nunito-sans max-w-md mb-8"
            >
              {t("notfound.page_not_found_message") || "The page you are looking for might have been removed, had its name changed, or is temporarily unavailable."}
            </motion.p>
            
            <motion.div 
              variants={itemVariants}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link 
                to="/"
                className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-full font-medium transition-colors duration-300 flex items-center justify-center gap-2 shadow-lg shadow-primary/20"
              >
                <FontAwesomeIcon icon={faHome} />
                {t("notfound.back_to_home") || "Back to Home"}
              </Link>
              
              <button
                onClick={() => window.history.back()}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-3 rounded-full font-medium transition-colors duration-300 flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                {t("notfound.go_back") || "Go Back"}
              </button>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default NotFound;